/**
 * AgroTech — Flight Data & Analysis  (Step 6 of the 9-step intelligence loop)
 *
 * Provides:
 *  1. Chunked and direct upload interface for drone telemetry and multispectral/thermal imagery
 *  2. Post-upload AI analysis trigger invoking AI diagnostics
 *  3. Per-zone AI narrative, sub-zone affected area mapping, and spectral correlation
 */

import React, { useState, useEffect } from 'react';
import {
  Microscope, Upload, Cpu, GitCompare,
  Layers, Thermometer, FileText, Navigation2,
  ChevronDown, ChevronUp, Info, CheckCircle2,
  AlertCircle, Clock, RefreshCw, ArrowRight, Sparkles,
  ShieldCheck, MapPin, CheckCircle,
} from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import Alert from '../../components/common/Alert';
import ChunkedUpload from '../../components/common/ChunkedUpload';
import { useNavigate } from 'react-router-dom';
import api, { agrotechDroneApi } from '../../services/api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Mission {
  id: string;
  name: string;
  status: string;
  mission_type: string;
  created_at: string;
  updated_at: string;
  metadata?: {
    anomaly_id?: string;
    boundary_name?: string;
    anomaly_type?: string;
    severity?: string;
    payload?: string;
    altitude_m?: number;
    ai_analysis_status?: string;
    diagnosis?: string;
    confidence?: number;
    affected_area_ha?: number;
    drone_analysis?: {
      diagnosis: string;
      category: string;
      severity: string;
      affected_area_ha: number;
      confidence: number;
      ndvi_drone_mean: number;
      ndvi_drone_depressed: number;
      satellite_match: boolean;
      correlation_notes: string;
      recommended_actions: Array<{ action: string; priority: string; remediation_type: string }>;
      narrative: string;
      ai_analysed_at: string;
    };
    step7_status?: string;
    [key: string]: any;
  };
}

export const FlightAnalysisPage: React.FC = () => {
  const navigate = useNavigate();
  const [missions, setMissions]           = useState<Mission[]>([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState<string | null>(null);
  const [success, setSuccess]             = useState<string | null>(null);
  const [selectedMission, setSelectedMission] = useState<Mission | null>(null);
  const [analysingId, setAnalysingId]     = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const [missionSearch, setMissionSearch] = useState('');
  const [missionFilter, setMissionFilter] = useState<'all' | 'complete' | 'pending'>('all');
  // Track completed upload session IDs so we can finalise the mission after all files land
  const [uploadedSessions, setUploadedSessions] = useState<string[]>([]);
  const [finalising, setFinalising]       = useState(false);

  const loadMissions = async () => {
    setLoading(true);
    try {
      const res = await api.get('/missions', { params: { limit: 100 } });
      const all: Mission[] = res.data?.data?.missions ?? res.data?.missions ?? [];
      const agrotechSurveys = all.filter(
        (m) => m.mission_type === 'drone_survey' || m.metadata?.step === 5 || m.metadata?.anomaly_id
      );
      setMissions(agrotechSurveys);
      if (agrotechSurveys.length > 0 && !selectedMission) {
        setSelectedMission(agrotechSurveys[0]);
      } else if (selectedMission) {
        const refreshed = agrotechSurveys.find(m => m.id === selectedMission.id);
        if (refreshed) setSelectedMission(refreshed);
      }
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to load survey missions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMissions();
  }, []);

  const handleRunAnalysis = async (missionId: string) => {
    setAnalysingId(missionId);
    setError(null);
    try {
      const res = await agrotechDroneApi.analyzeDroneFlight(missionId);
      setSuccess('AI Drone Analysis complete! Findings and correlation narrative updated.');
      await loadMissions();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to execute AI drone analysis');
    } finally {
      setAnalysingId(null);
    }
  };

  // Called by ChunkedUpload each time one file finishes
  const handleFileUploaded = (sessionId: string, filePath: string) => {
    setUploadedSessions(prev => [...prev, sessionId]);
    setUploadSuccess(`File sealed — SHA-256 verified. Session ${sessionId.slice(0, 8)}… stored.`);
  };

  // Called when operator clicks "Confirm Upload Complete" after all files are uploaded
  const handleFinaliseUpload = async () => {
    if (!selectedMission || uploadedSessions.length === 0) return;
    setFinalising(true);
    setError(null);
    try {
      await api.patch(`/missions/${selectedMission.id}`, {
        status: 'completed',
        metadata: {
          ...selectedMission.metadata,
          telemetry_uploaded: true,
          files_uploaded_at: new Date().toISOString(),
          file_count: uploadedSessions.length,
          upload_session_ids: uploadedSessions,
        },
      });
      setUploadSuccess(`${uploadedSessions.length} file${uploadedSessions.length > 1 ? 's' : ''} ingested and mission marked ready for AI analysis.`);
      setUploadedSessions([]);
      await loadMissions();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to finalise upload');
    } finally {
      setFinalising(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Microscope className="w-7 h-7 text-purple-600" /> Flight Data & AI Analysis
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Step 6 — Ingest drone telemetry, run multispectral/thermal AI diagnosis, and generate per-zone narrative
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={loadMissions} className="flex items-center gap-1.5">
          <RefreshCw className="w-4 h-4" /> Refresh
        </Button>
      </div>

      {error && <Alert type="error" onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert type="success" onClose={() => setSuccess(null)}>{success}</Alert>}
      {uploadSuccess && <Alert type="success" onClose={() => setUploadSuccess(null)}>{uploadSuccess}</Alert>}

      {missions.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <Navigation2 className="mx-auto w-12 h-12 text-gray-400 mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">No Drone Survey Missions Found</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto mt-1 mb-4">
              Missions are dispatched from Step 5 — Survey Mission Planner when satellite anomalies are confirmed.
            </p>
            <Button variant="primary" onClick={() => navigate('/agrotech/survey-planner')}>
              Go to Step 5 — Survey Mission Planner →
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          {/* ── Left: Survey Missions List (sticky, independently scrollable) ── */}
          <div className="lg:sticky lg:top-4 lg:max-h-[calc(100vh-10rem)] lg:overflow-y-auto lg:pr-1 flex flex-col gap-2">
            {/* Header + search */}
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Survey Missions ({missions.length})
              </p>
            </div>
            <input
              type="search"
              placeholder="Search missions…"
              value={missionSearch}
              onChange={(e) => setMissionSearch(e.target.value)}
              className="w-full px-2.5 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-400"
            />
            {/* Status filter pills */}
            <div className="flex gap-1.5 flex-wrap">
              {(['all', 'complete', 'pending'] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setMissionFilter(f)}
                  className={`px-2 py-0.5 rounded-full text-[11px] font-medium border transition-colors ${
                    missionFilter === f
                      ? 'bg-purple-600 border-purple-600 text-white'
                      : 'border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-purple-400'
                  }`}
                >
                  {f === 'all' ? 'All' : f === 'complete' ? 'AI Diagnosed' : 'Pending'}
                </button>
              ))}
            </div>
            {/* Compact list */}
            <div className="divide-y divide-gray-100 dark:divide-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              {missions
                .filter((m) => {
                  const matchSearch = !missionSearch || m.name.toLowerCase().includes(missionSearch.toLowerCase());
                  const matchFilter =
                    missionFilter === 'all' ||
                    (missionFilter === 'complete' && m.metadata?.ai_analysis_status === 'complete') ||
                    (missionFilter === 'pending'  && m.metadata?.ai_analysis_status !== 'complete');
                  return matchSearch && matchFilter;
                })
                .map((m) => {
                  const isSelected  = selectedMission?.id === m.id;
                  const hasAnalysis = m.metadata?.ai_analysis_status === 'complete';
                  return (
                    <button
                      key={m.id}
                      onClick={() => setSelectedMission(m)}
                      className={[
                        'w-full text-left flex items-center gap-0 transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-purple-400',
                        isSelected
                          ? 'bg-purple-50 dark:bg-purple-900/15'
                          : 'bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800/60',
                      ].join(' ')}
                    >
                      {/* Selected accent bar */}
                      <span className={`self-stretch w-0.5 flex-shrink-0 rounded-r ${isSelected ? 'bg-purple-500' : 'bg-transparent'}`} />
                      <span className="flex-1 min-w-0 px-3 py-2.5">
                        <span className="flex items-center justify-between gap-1.5 mb-0.5">
                          <span className="text-xs font-semibold text-gray-900 dark:text-white truncate">{m.name}</span>
                          <span className={`flex-shrink-0 w-1.5 h-1.5 rounded-full ${hasAnalysis ? 'bg-green-500' : 'bg-amber-400'}`} />
                        </span>
                        <span className="text-[11px] text-gray-400 truncate block">
                          {m.metadata?.payload || 'Multispectral'} · {m.metadata?.altitude_m || 75}m · {new Date(m.created_at).toLocaleDateString()}
                        </span>
                      </span>
                    </button>
                  );
                })}
              {missions.filter((m) => {
                const matchSearch = !missionSearch || m.name.toLowerCase().includes(missionSearch.toLowerCase());
                const matchFilter = missionFilter === 'all' || (missionFilter === 'complete' && m.metadata?.ai_analysis_status === 'complete') || (missionFilter === 'pending' && m.metadata?.ai_analysis_status !== 'complete');
                return matchSearch && matchFilter;
              }).length === 0 && (
                <p className="px-3 py-4 text-xs text-gray-400 text-center">No missions match</p>
              )}
            </div>
          </div>

          {/* ── Right: Mission Details, Upload & AI Analysis (independently scrollable) ── */}
          <div className="lg:col-span-2 space-y-6 lg:max-h-[calc(100vh-10rem)] lg:overflow-y-auto lg:pr-1">
            {selectedMission && (
              <>
                {/* ── Mission Ingestion & Upload Card ── */}
                <Card>
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-4 pb-3 border-b border-gray-100 dark:border-gray-800">
                    <div>
                      <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                        <Layers className="w-5 h-5 text-purple-600" /> {selectedMission.name}
                      </h2>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        Target Field: {selectedMission.metadata?.boundary_name || 'Assigned Field'} · Anomaly: {selectedMission.metadata?.anomaly_type || 'Vegetation Decline'}
                      </p>
                    </div>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleRunAnalysis(selectedMission.id)}
                      disabled={analysingId === selectedMission.id}
                      className="flex items-center gap-1.5"
                    >
                      {analysingId === selectedMission.id ? (
                        <><Spinner size="sm" /> Analysing Telemetry…</>
                      ) : (
                        <><Sparkles className="w-4 h-4" /> Trigger AI Drone Analysis</>
                      )}
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs mb-4">
                    <div className="p-3 bg-gray-50 dark:bg-gray-800/60 rounded-lg">
                      <span className="text-gray-400 block mb-1">Payload Sensor</span>
                      <span className="font-semibold text-gray-900 dark:text-white">{selectedMission.metadata?.payload || 'Multispectral (NDRE/NDVI)'}</span>
                    </div>
                    <div className="p-3 bg-gray-50 dark:bg-gray-800/60 rounded-lg">
                      <span className="text-gray-400 block mb-1">Flight Altitude</span>
                      <span className="font-semibold text-gray-900 dark:text-white">{selectedMission.metadata?.altitude_m || 75} m AGL</span>
                    </div>
                    <div className="p-3 bg-gray-50 dark:bg-gray-800/60 rounded-lg">
                      <span className="text-gray-400 block mb-1">Target Area</span>
                      <span className="font-semibold text-gray-900 dark:text-white">{selectedMission.metadata?.affected_area_hectares || 2.4} ha</span>
                    </div>
                  </div>

                  {/* ── Real chunked upload ── */}
                  <div>
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                      <Upload className="w-3.5 h-3.5" /> Upload Flight Data Package
                    </p>
                    <p className="text-[11px] text-gray-400 mb-3">
                      Multispectral TIFF, Thermal RJPEG, Flight Logs (.CSV / .KML) · SHA-256 seal generated per file
                    </p>
                    <ChunkedUpload
                      accept=".tif,.tiff,.rjpeg,.jpg,.jpeg,.csv,.kml,.gpx,.pdf"
                      maxSizeMB={10240}
                      onComplete={handleFileUploaded}
                      onError={(msg) => setError(msg)}
                    />
                    {uploadedSessions.length > 0 && (
                      <div className="mt-3 flex items-center justify-between p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                        <span className="text-xs text-green-800 dark:text-green-200 flex items-center gap-1.5">
                          <CheckCircle className="w-3.5 h-3.5" />
                          {uploadedSessions.length} file{uploadedSessions.length > 1 ? 's' : ''} sealed and ready
                        </span>
                        <Button size="sm" onClick={handleFinaliseUpload} loading={finalising} disabled={finalising}>
                          {finalising ? 'Saving…' : 'Confirm Upload Complete'}
                        </Button>
                      </div>
                    )}
                  </div>
                </Card>

                {/* ── AI Findings & Narrative Section ── */}
                {selectedMission.metadata?.drone_analysis ? (
                  <div className="space-y-4">
                    <Card>
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <Cpu className="w-5 h-5 text-purple-600" />
                          <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                            Drone-Derived AI Diagnosis
                          </h3>
                        </div>
                        <span className="text-xs font-semibold px-2 py-0.5 rounded bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">
                          Confidence: {Math.round(selectedMission.metadata.drone_analysis.confidence * 100)}%
                        </span>
                      </div>

                      {/* Primary diagnosis banner */}
                      <div className="p-3.5 rounded-lg bg-purple-50 dark:bg-purple-950/40 border border-purple-200 dark:border-purple-800 mb-4">
                        <p className="text-xs font-semibold uppercase tracking-wider text-purple-600 dark:text-purple-400 mb-1">
                          Confirmed Agronomic Issue
                        </p>
                        <p className="text-sm font-bold text-purple-950 dark:text-purple-200">
                          {selectedMission.metadata.drone_analysis.diagnosis}
                        </p>
                      </div>

                      {/* Side-by-side metrics */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs mb-4">
                        <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800">
                          <span className="text-gray-400 block">Drone Mean NDVI</span>
                          <span className="text-base font-bold text-gray-900 dark:text-white">{selectedMission.metadata.drone_analysis.ndvi_drone_mean}</span>
                        </div>
                        <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800">
                          <span className="text-gray-400 block">Depressed Sub-zone NDVI</span>
                          <span className="text-base font-bold text-red-600">{selectedMission.metadata.drone_analysis.ndvi_drone_depressed}</span>
                        </div>
                        <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800">
                          <span className="text-gray-400 block">Refined Affected Area</span>
                          <span className="text-base font-bold text-amber-600">{selectedMission.metadata.drone_analysis.affected_area_ha} ha</span>
                        </div>
                        <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800">
                          <span className="text-gray-400 block">Satellite Match</span>
                          <span className="text-base font-bold text-green-600">{selectedMission.metadata.drone_analysis.satellite_match ? '100% Verified' : 'Divergent'}</span>
                        </div>
                      </div>

                      {/* Satellite Correlation Notes */}
                      <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900 mb-4 text-xs">
                        <p className="font-semibold text-blue-900 dark:text-blue-300 mb-1 flex items-center gap-1.5">
                          <GitCompare className="w-3.5 h-3.5" /> Satellite-to-Drone Spatial Correlation
                        </p>
                        <p className="text-blue-800 dark:text-blue-200">
                          {selectedMission.metadata.drone_analysis.correlation_notes}
                        </p>
                      </div>

                      {/* Post-Flight Technical Narrative */}
                      <div className="mb-4">
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                          Post-Flight AI Technical Narrative
                        </h4>
                        <div className="p-3.5 rounded-lg bg-gray-50 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 text-xs text-gray-700 dark:text-gray-300 leading-relaxed">
                          {selectedMission.metadata.drone_analysis.narrative}
                        </div>
                      </div>

                      {/* Recommended Actions */}
                      {selectedMission.metadata.drone_analysis.recommended_actions?.length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                            AI Recommended Remediation Interventions
                          </h4>
                          <div className="space-y-2">
                            {selectedMission.metadata.drone_analysis.recommended_actions.map((act, i) => (
                              <div key={i} className="flex items-start justify-between gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs">
                                <div className="flex items-start gap-2">
                                  <CheckCircle2 className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                                  <span className="text-gray-900 dark:text-white font-medium">{act.action}</span>
                                </div>
                                <span className="px-2 py-0.5 rounded bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 uppercase font-semibold text-[10px]">
                                  {act.remediation_type.replace(/_/g, ' ')}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Step 7 Navigation CTA */}
                      <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-800 flex justify-end">
                        <Button
                          variant="primary"
                          onClick={() => navigate('/agrotech/analysis-review')}
                          className="flex items-center gap-1.5"
                        >
                          Proceed to Step 7 — Analysis Review <ArrowRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </Card>
                  </div>
                ) : (
                  <Card>
                    <div className="text-center py-8 text-gray-400 text-xs">
                      <Cpu className="mx-auto w-8 h-8 text-gray-300 dark:text-gray-600 mb-2" />
                      No AI analysis run on this mission yet. Click "Trigger AI Drone Analysis" above to process spectral bands.
                    </div>
                  </Card>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FlightAnalysisPage;
// Made with Bob
